import React, { useState } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import DeletedItemTable from "./deleted";
import ProductsTable from "./all";
import ModifiersList from "./modifiers";
import { oneBranchHasPermission } from "src/utils";
import { MenuPermissions } from "src/constants/permissions";
import NoAccessPage from "src/pages/no-access";
import CategoriesTable from "./categories";
import { useMediaQuery } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

interface TabProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }} height={"40px"}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </Box>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

// Map paths to tab indices
const tabPaths = [
  "/menu/products",
  "/menu/categories",
  "/menu/modifiers",
  "/menu/deleted",
];

const MenuTabs = () => {
  const { t } = useTranslation("inventory");
  const isSmallScreen = useMediaQuery("(max-width:700px)");
  const location = useLocation();
  const navigate = useNavigate();

  const currentPath = location.pathname;

  // Find the tab index based on the current URL path
  const currentTabIndex = tabPaths.indexOf(currentPath);
  const [value, setValue] = useState(
    currentTabIndex === -1 ? 0 : currentTabIndex
  );

  const labels = [t("products"), t("categories"), t("modifiers"), t("deleted")];

  // permissions
  const viewProducts = oneBranchHasPermission(MenuPermissions.viewProducts);
  const viewModifiers = oneBranchHasPermission(MenuPermissions.viewModifiers);
  const viewCategories = oneBranchHasPermission(MenuPermissions.viewCategories);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    navigate(tabPaths[newValue]); // Change the URL based on the selected tab
  };

  return (
    <Box>
      <Box
        bgcolor={"#F9FAFB"}
        p={0.5}
        border={"1px solid  #EAECF0"}
        borderRadius={1}
        width={{ xs: "100%", md: "fit-content" }}
        display={"flex"}
        justifyContent={isSmallScreen ? "center" : ""}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          TabIndicatorProps={{ style: { display: "none" } }}
          orientation={isSmallScreen ? "vertical" : "horizontal"}
          sx={{
            minHeight: { xs: "100%", md: "35px" },
            height: { xs: "100%", md: "35px" },
          }}
        >
          {labels.map((label, index) => {
            return (
              <Tab
                disableRipple
                key={index}
                sx={{
                  borderRadius: "6px",
                  color: "#667085",
                  height: "33px",
                  minHeight: "33px",
                  "&.Mui-selected": {
                    bgcolor: "#FFFFFF",
                    color: "#344054",
                    boxShadow: "0px 1px 3px 0px rgba(0, 0, 0, 0.12)",
                    m: "1px",
                  },
                }}
                label={label}
                {...a11yProps(index)}
              />
            );
          })}
        </Tabs>
      </Box>

      <Box mt={{ xs: "-20px", md: "-65px" }}>
        <CustomTabPanel value={value} index={0}>
          {viewProducts ? <ProductsTable /> : <NoAccessPage />}
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          {viewCategories ? <CategoriesTable /> : <NoAccessPage />}
        </CustomTabPanel>
        <CustomTabPanel value={value} index={2}>
          {viewModifiers ? <ModifiersList /> : <NoAccessPage />}
        </CustomTabPanel>
        <CustomTabPanel value={value} index={3}>
          {viewProducts || viewModifiers ? (
            <DeletedItemTable />
          ) : (
            <NoAccessPage />
          )}
        </CustomTabPanel>
      </Box>
    </Box>
  );
};

export default MenuTabs;
